/**
 * fieldMappingApplyLater — apply a Field Mappings set to items already imported.
 *
 * Every Miro import remembers what each sticky said about its item
 * (item_source_attributes, through recordSourceAttributes). This plans what a
 * set would do to those items with the same applier an import uses
 * (fieldMappingApply.applyMappingSet), then writes through the same paths:
 * acceptValueDirectly for attribute values, the items store for fields.
 *
 *   attribute value → set when the item has none; update when a mapping set
 *                     wrote the current one (its note starts with
 *                     MAPPING_NOTE_PREFIX) and it differs; leave alone when a
 *                     person set it; skip when the workspace lacks the attribute.
 *   item field      → category, altitude, strategic bucket only when empty;
 *                     tags: add the missing ones. Never title or description.
 *
 * Items imported before anything was recorded can be matched to a board read
 * again (readMiroBoardAgain): one sticky per item, by title.
 */

import { CONNECTOR_FIELDS, type FieldMappingSet } from './fieldMappingRegistry';
import {
  acceptMappedAttribute,
  applyMappingSet,
  countMissingAttribute,
  countSkippedFields,
  countUnmatched,
  countFailure,
  describeMappingProblems,
  isBlank,
  itemCreateFieldsFromMapping,
  mappingValueNote,
  newMappingProblems,
  plural,
  sameValue,
  writtenByMapping,
  type AcceptDirectly,
  type MappedAttributeValue,
  type MappingProblems,
} from './fieldMappingApply';
import {
  miroStickyExternalValues,
  miroStickyId,
  miroStickySourceRows,
  workspaceAttributeKeys,
  type MiroStickyRecord,
} from './miroImportMapping';
import { titleSourceKey } from './importBatch';
import { getIntegrationsApi } from './integrationsApi';
import { listSourceAttributes, recordSourceAttributes } from './semanticConnectors/unifiedAttributesService';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import { useUserStore } from '../stores/prioritization/userStore';

/** The note on a value written by applying a set later. */
export const APPLY_LATER_NOTE_WHEN = 'applied later';

// ── The plan (pure) ───────────────────────────────────────────────────────────

/** What a source said about one item, keyed by the connector's Field Mappings keys. */
export interface RecordedSource {
  itemId: string;
  values: Record<string, unknown>;
}

/** The item fields the plan reads. */
export interface PlannableItem {
  id: string;
  itemCategory?: string | null;
  altitudeLevel?: string | number | null;
  strategicBucket?: string | null;
  tags?: string[] | null;
}

export interface CurrentAcceptedValue {
  value: unknown;
  note?: string | null;
}

/** set: the item has none · update: a set wrote it and it differs · leave: a person set it · same: already that value. */
export type AttributeAction = 'set' | 'update' | 'leave' | 'same';

export interface ItemPlan {
  itemId: string;
  attributes: Array<{ mapped: MappedAttributeValue; action: AttributeAction }>;
  /** Empty item fields to fill, keyed as the items store names them (itemCategory, altitudeLevel, strategicBucket). */
  fields: Record<string, string>;
  /** The item's tags with the missing ones added; absent when none are missing. */
  tags?: string[];
}

export interface MappingSetPlan {
  set: FieldMappingSet;
  connectorType: string;
  /** One per recorded item that still exists. */
  items: ItemPlan[];
  /** Missing attributes, values the set doesn't list, fields it can't set. */
  problems: MappingProblems;
}

/** Item fields a later apply never touches. */
const NEVER_LATER = new Set(['title', 'description']);

export function planMappingSetForImported(input: {
  set: FieldMappingSet;
  connectorType: string;
  recorded: ReadonlyArray<RecordedSource>;
  item: (itemId: string) => PlannableItem | undefined;
  acceptedValue: (itemId: string, attributeKey: string) => CurrentAcceptedValue | undefined;
  attributeKeys: ReadonlySet<string>;
}): MappingSetPlan {
  const plan: MappingSetPlan = { set: input.set, connectorType: input.connectorType, items: [], problems: newMappingProblems() };
  for (const source of input.recorded) {
    const item = input.item(source.itemId);
    if (!item) continue;
    const applied = applyMappingSet(source.values, input.set);
    countUnmatched(plan.problems, input.connectorType, applied.unmatched);

    const attributes: ItemPlan['attributes'] = [];
    for (const mapped of applied.attributes) {
      if (!input.attributeKeys.has(mapped.attributeKey)) {
        countMissingAttribute(plan.problems, mapped.attributeKey);
        continue;
      }
      const current = input.acceptedValue(item.id, mapped.attributeKey);
      const action: AttributeAction =
        !current || isBlank(current.value) ? 'set'
          : !writtenByMapping(current.note) ? 'leave'
            : sameValue(current.value, mapped.value) ? 'same'
              : 'update';
      attributes.push({ mapped, action });
    }

    const created = itemCreateFieldsFromMapping(applied.fields);
    countSkippedFields(plan.problems, created.skipped);
    const fields: Record<string, string> = {};
    for (const [field, value] of Object.entries(created.fields)) {
      if (NEVER_LATER.has(field)) continue;
      if (isBlank((item as unknown as Record<string, unknown>)[field])) fields[field] = value;
    }
    const have = item.tags ?? [];
    const missingTags = created.tags.filter((t) => !have.includes(t));

    plan.items.push({
      itemId: item.id,
      attributes,
      fields,
      ...(missingTags.length > 0 ? { tags: [...have, ...missingTags] } : {}),
    });
  }
  return plan;
}

// ── Counting and words ────────────────────────────────────────────────────────

export interface ApplyLaterCounts {
  /** Recorded items the set was run over. */
  items: number;
  /** Items given at least one value they did not have. */
  given: number;
  /** Items with a value a set wrote before that changes. */
  changed: number;
  /** Items with a value a person set, left alone. */
  leftAlone: number;
}

function cloneProblems(p: MappingProblems): MappingProblems {
  return {
    missingAttribute: new Map(p.missingAttribute),
    unmatched: new Map(p.unmatched),
    notSettable: new Map(p.notSettable),
    notAllowed: new Map(p.notAllowed),
    failed: p.failed,
    firstFailure: p.firstFailure,
  };
}

function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) return clauses.join('');
  return `${clauses.slice(0, -1).join(', ')}, and ${clauses[clauses.length - 1]}`;
}

/**
 * "42 items came from Miro. This set would give 30 of them a value, change 3 it
 * set before, and leave 4 alone because a person set them." — then the same
 * problem sentences an import shows. `done` switches to what it did.
 */
export function describeApplyLater(
  counts: ApplyLaterCounts,
  problems: MappingProblems,
  sourceLabel: string,
  done: boolean,
): { message: string; hasProblems: boolean } {
  const head = `${plural(counts.items, 'item')} came from ${sourceLabel}.`;
  const clauses: string[] = [];
  if (counts.given > 0) clauses.push(`${done ? 'gave' : 'give'} ${counts.given} of them a value`);
  if (counts.changed > 0) clauses.push(`${done ? 'changed' : 'change'} ${counts.changed} it set before`);
  if (counts.leftAlone > 0) clauses.push(`${done ? 'left' : 'leave'} ${counts.leftAlone} alone because a person set them`);
  const body = clauses.length > 0
    ? `This set ${done ? '' : 'would '}${joinClauses(clauses)}.`
    : `This set ${done ? 'changed' : 'would change'} nothing.`;
  const described = describeMappingProblems(problems);
  return { message: [head, body, ...described.sentences].join(' '), hasProblems: described.hasProblems };
}

export interface ApplyLaterResult {
  counts: ApplyLaterCounts;
  problems: MappingProblems;
  message: string;
  hasProblems: boolean;
}

export interface ApplyLaterDeps {
  acceptedBy: string | null;
  accept: AcceptDirectly;
  updateItem: (itemId: string, patch: Record<string, unknown>) => void;
}

/**
 * Run a plan. `dryRun` only counts what it would do; otherwise each value is
 * written (note: "Field mapping applied later — …") and the counts say what
 * actually saved.
 */
export async function runMappingSetOnImported(
  plan: MappingSetPlan,
  deps: ApplyLaterDeps,
  opts: { dryRun: boolean; sourceLabel: string },
): Promise<ApplyLaterResult> {
  const problems = cloneProblems(plan.problems);
  const counts: ApplyLaterCounts = { items: plan.items.length, given: 0, changed: 0, leftAlone: 0 };
  for (const item of plan.items) {
    let given = false;
    let changed = false;
    if (item.attributes.some((a) => a.action === 'leave')) counts.leftAlone++;
    for (const { mapped, action } of item.attributes) {
      if (action !== 'set' && action !== 'update') continue;
      const saved = opts.dryRun || await acceptMappedAttribute(
        deps,
        item.itemId,
        mapped,
        mappingValueNote(APPLY_LATER_NOTE_WHEN, plan.connectorType, mapped, plan.set.name),
        problems,
      );
      if (saved && action === 'set') given = true;
      if (saved && action === 'update') changed = true;
    }
    const patch: Record<string, unknown> = { ...item.fields, ...(item.tags ? { tags: item.tags } : {}) };
    if (Object.keys(patch).length > 0) {
      if (opts.dryRun) given = true;
      else {
        try {
          deps.updateItem(item.itemId, patch);
          given = true;
        } catch (err) {
          countFailure(problems, err instanceof Error ? err.message : undefined);
        }
      }
    }
    if (given) counts.given++;
    if (changed) counts.changed++;
  }
  return { counts, problems, ...describeApplyLater(counts, problems, opts.sourceLabel, !opts.dryRun) };
}

// ── Matching a board read again to items (pure) ───────────────────────────────

export interface TitleMatchResult<R> {
  matched: Array<{ record: R; itemId: string }>;
  /** Records whose text equals no item's title. */
  unmatched: number;
  /** Records that equal more than one item's title, or share an item with another record. */
  ambiguous: number;
}

/**
 * One record per item, by title: a record's text (trimmed, spaces collapsed,
 * any case) equals the item's title. Only one-to-one matches count. A record
 * can offer several texts (the full text, and the shortened form an import
 * used as the title); any of them may match.
 */
export function matchRecordsToItemsByTitle<R>(
  records: readonly R[],
  textsOf: (record: R) => Array<string | null | undefined>,
  items: ReadonlyArray<{ id: string; title: string }>,
): TitleMatchResult<R> {
  // titleSourceKey with no source is the normalized title alone.
  const key = (text: string) => titleSourceKey(text, null);
  const itemsByKey = new Map<string, string[]>();
  for (const item of items) {
    if (!item.title?.trim()) continue;
    const k = key(item.title);
    itemsByKey.set(k, [...(itemsByKey.get(k) ?? []), item.id]);
  }
  const out: TitleMatchResult<R> = { matched: [], unmatched: 0, ambiguous: 0 };
  const claims = new Map<string, R[]>();
  for (const record of records) {
    const candidates = new Set<string>();
    for (const text of textsOf(record)) {
      if (!text?.trim()) continue;
      for (const id of itemsByKey.get(key(text)) ?? []) candidates.add(id);
    }
    if (candidates.size === 0) out.unmatched++;
    else if (candidates.size > 1) out.ambiguous++;
    else {
      const [itemId] = candidates;
      claims.set(itemId, [...(claims.get(itemId) ?? []), record]);
    }
  }
  for (const [itemId, claimers] of claims) {
    if (claimers.length === 1) out.matched.push({ record: claimers[0], itemId });
    else out.ambiguous += claimers.length;
  }
  return out;
}

// ── Wired to the stores ───────────────────────────────────────────────────────

export interface ImportedForMapping {
  /** Live items with recorded values from this connector. */
  recorded: RecordedSource[];
  /** Live items whose source names the connector but that have nothing recorded. */
  unrecorded: Array<{ id: string; title: string }>;
}

/** What each live item from a connector said, and which of its items have nothing recorded. */
export async function loadImportedForMapping(workspaceId: string, connectorType: string): Promise<ImportedForMapping> {
  if (!useItemsStore.getState().syncState.lastSyncAt) await useItemsStore.getState().fetchFromSupabase();
  const keys = new Set((CONNECTOR_FIELDS[connectorType] ?? []).map((f) => f.key));
  const byItem = new Map<string, Record<string, unknown>>();
  for (const row of await listSourceAttributes(workspaceId, { connectorType })) {
    if (row.sourceKind !== 'field' || !keys.has(row.sourceKey)) continue;
    byItem.set(row.itemId, { ...(byItem.get(row.itemId) ?? {}), [row.sourceKey]: row.rawValue });
  }
  const live = useItemsStore.getState().items.filter((i) => (i.exit_status ?? 'active') !== 'archived');
  return {
    recorded: live.filter((i) => byItem.has(i.id)).map((i) => ({ itemId: i.id, values: byItem.get(i.id) ?? {} })),
    unrecorded: live
      .filter((i) => !byItem.has(i.id) && (i.source ?? '').toLowerCase().includes(connectorType.toLowerCase()))
      .map((i) => ({ id: i.id, title: i.title })),
  };
}

/** Plan a set over the workspace's imported items, from the stores. */
export async function planMappingSetForWorkspace(
  workspaceId: string,
  connectorType: string,
  set: FieldMappingSet,
): Promise<{ plan: MappingSetPlan; unrecorded: ImportedForMapping['unrecorded'] }> {
  const [imported, attributeKeys] = await Promise.all([
    loadImportedForMapping(workspaceId, connectorType),
    workspaceAttributeKeys(),
  ]);
  if (!useValueProposalStore.getState().syncState.lastSyncAt) await useValueProposalStore.getState().fetchFromSupabase();
  const values = useValueProposalStore.getState();
  const items = useItemsStore.getState();
  const plan = planMappingSetForImported({
    set,
    connectorType,
    recorded: imported.recorded,
    item: (id) => items.getItemById(id),
    acceptedValue: (id, key) => {
      const av = values.getAcceptedValue(id, key);
      return av ? { value: av.value, note: av.acceptanceNote } : undefined;
    },
    attributeKeys,
  });
  return { plan, unrecorded: imported.unrecorded };
}

/** Run a plan through the same writes an import uses. */
export function applyMappingSetToImported(
  plan: MappingSetPlan,
  opts: { dryRun: boolean; sourceLabel: string },
): Promise<ApplyLaterResult> {
  return runMappingSetOnImported(
    plan,
    {
      acceptedBy: useUserStore.getState().userId || null,
      accept: (params) => useValueProposalStore.getState().acceptValueDirectly(params),
      updateItem: (id, patch) => useItemsStore.getState().updateItem(id, patch),
    },
    opts,
  );
}

export interface BoardReadResult {
  matched: number;
  unmatched: number;
  ambiguous: number;
  /** Set when the matched stickies' values could not be saved. */
  recordError?: string;
}

/**
 * Read a Miro board again (the import's read call; nothing is created), match
 * its stickies to Miro items that have nothing recorded, and remember what each
 * matched sticky said — so the set can then be applied to those items too.
 */
export async function readMiroBoardAgain(
  workspaceId: string,
  board: { id: string; name?: string },
  unrecorded: ReadonlyArray<{ id: string; title: string }>,
): Promise<BoardReadResult> {
  const result = await getIntegrationsApi(workspaceId).importFromMiro(board.id);
  const stickies: MiroStickyRecord[] = result.items ?? [];
  if (!result.success && stickies.length === 0) throw new Error('Miro did not return the board’s stickies.');
  const context = { boardName: board.name };
  const match = matchRecordsToItemsByTitle(
    stickies,
    (s) => {
      const text = miroStickyExternalValues(s, context).sticky_text as string | undefined;
      // An import titled the item with the sticky's label (simple) or first 100 characters (zones).
      return [text, s.label, text?.substring(0, 100)];
    },
    unrecorded,
  );
  const rows = match.matched.flatMap(({ record, itemId }) =>
    miroStickySourceRows(itemId, miroStickyId(record), miroStickyExternalValues(record, context)),
  );
  const recorded = await recordSourceAttributes({ workspaceId, connectorType: 'miro', origin: 'sync', rows });
  return {
    matched: match.matched.length,
    unmatched: match.unmatched,
    ambiguous: match.ambiguous,
    ...(recorded.error ? { recordError: recorded.error } : {}),
  };
}
